import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { JobTypeService } from '@app/features/job-types/job-type-service';
import { JobType } from '@app/features/job-types/job-type.model';
import {
  SalaryScaleDialog,
  SalaryScaleDialogData,
} from '@app/features/salary/salary-scale-dialog/salary-scale-dialog';
import { SalaryScaleUiService } from '@app/features/salary/salary-scale-ui-service';
import { SalaryScale } from '@app/features/salary/salary-scale.model';
import { SalaryScalesService } from '@app/features/salary/salary-scales-service';
import { AUTHENTICATED_USER_SERVICE } from '@app/shared/auth/authenticated-user-service/authenticated-user-service.token';
import { HasRoleDirective } from '@app/shared/auth/has-role.directive';
import { RxjsDirective } from '@app/shared/rxjs.directive';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faHandHoldingDollar } from '@fortawesome/free-solid-svg-icons';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-menu-salary-scale',
  imports: [
    CommonModule,
    MatButtonModule,
    MatTooltip,
    MatMenuModule,
    MatIconModule,
    FontAwesomeModule,
    HasRoleDirective,
  ],
  templateUrl: './menu-salary-scale.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./menu-salary-scale.scss'],
})
export class MenuSalaryScale extends RxjsDirective implements OnInit {
  readonly cdr = inject(ChangeDetectorRef);
  readonly dialog = inject(MatDialog);
  readonly faHandHoldingDollar = faHandHoldingDollar;
  readonly jobTypeService = inject(JobTypeService);
  readonly salaryScaleService = inject(SalaryScalesService);
  readonly salaryScaleUiService = inject(SalaryScaleUiService);
  readonly authenticatedUserService = inject(AUTHENTICATED_USER_SERVICE);

  jobTypes: JobType[] = [];
  salaryScalesByJobTypeId: Record<number, SalaryScale[]> = {};

  ngOnInit(): void {
    this.register(this.initJobTypes(), this.initSalaryScales());
  }

  private initJobTypes(): Subscription {
    return this.jobTypeService.allJobTypes$.subscribe({
      next: (response: JobType[]) => {
        this.jobTypes = response;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching job types', err);
      },
    });
  }

  private initSalaryScales(): Subscription {
    return this.salaryScaleService.list({ unpaged: true }).subscribe({
      next: (response) => {
        this.salaryScalesByJobTypeId = response.content.reduce(
          (acc, salaryScale) => {
            if (salaryScale.jobTypeId) {
              if (!acc[salaryScale.jobTypeId]) {
                acc[salaryScale.jobTypeId] = [];
              }
              acc[salaryScale.jobTypeId].push(salaryScale);
            }
            return acc;
          },
          {} as Record<number, SalaryScale[]>,
        );
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching salary scales', err);
      },
    });
  }

  openCreateSalaryScaleDialog(jobType: JobType): void {
    const dialogRef = this.dialog.open<SalaryScaleDialog, SalaryScaleDialogData, SalaryScale>(
      SalaryScaleDialog,
      {
        width: '80%',
        minWidth: '230px',
        maxWidth: '512px',
        disableClose: true,
        data: { jobType },
      },
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        // Update the salary scales for the job type
        if (!this.salaryScalesByJobTypeId[jobType.id!]) {
          this.salaryScalesByJobTypeId[jobType.id!] = [];
        }
        this.salaryScalesByJobTypeId[jobType.id!].push(result);
        this.cdr.detectChanges();
      }
    });
  }

  openEditSalaryScaleDialog(jobType: JobType, salaryScale: SalaryScale): void {
    const dialogRef = this.salaryScaleUiService.openEditSalaryScaleDialog(jobType, salaryScale);
    dialogRef.afterClosed().subscribe((result) => {
      if (result !== undefined) {
        // Update the salary scales for the job type
        if (!this.salaryScalesByJobTypeId[jobType.id!]) {
          this.salaryScalesByJobTypeId[jobType.id!] = [];
        }
        const index = this.salaryScalesByJobTypeId[jobType.id!].findIndex(
          (s) => s.id === salaryScale.id,
        );
        if (index !== -1) {
          this.salaryScalesByJobTypeId[jobType.id!][index] = result;
        } else {
          this.salaryScalesByJobTypeId[jobType.id!].push(result);
        }
        this.cdr.detectChanges();
      }
    });
  }
}
